import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbModal, NgbToast } from '@ng-bootstrap/ng-bootstrap';

import { GeneralRoutePaths, SettingsRoutePath } from '../../../core/enums/route-types.enum';
import { ToastPositionEnum } from '../../../core/enums/toast-position.enum';
import { CoverageSessionService } from '../../../core/services/coverage/coverage-session.service';
import { isMobile } from '../../../core/util/feature-detection.helper';
import { ToastSettingsService } from '../toast-settings.service';
import { Toast, ToastService } from '../toast.service';

@Component({
  selector: 'app-toast-container',
  templateUrl: './toast-container.component.html',
  styleUrls: ['./toast-container.component.scss'],
  host: {
    '[class]': 'hostClass()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgTemplateOutlet, NgbToast],
})
export class ToastContainerComponent {
  private toastService = inject(ToastService);
  private toastSettingsService = inject(ToastSettingsService);
  private coverageSessionService = inject(CoverageSessionService);
  private modalService = inject(NgbModal);
  private router = inject(Router);

  toasts = this.toastService.toasts;
  showShortcutInToast = this.toastSettingsService.showShortcutInToast;
  isCoveringActive = toSignal(this.coverageSessionService.isCoveringActiveObservable(), {
    requireSync: true,
  });
  isMobile = isMobile();

  orderedToasts = computed(() => {
    const toasts = this.toasts();
    const position = this.toastSettingsService.position();

    // bottom positions - newest toast is put at the bottom
    // top positions - newest toast is put at the top
    return position.startsWith('bottom-') ? toasts : [...toasts].reverse();
  });

  hostClass = computed(() => `toast-container position-fixed ${this.positionClasses[this.toastSettingsService.position()]}`);

  private positionClasses: Record<ToastPositionEnum, string> = {
    [ToastPositionEnum.TOP_LEFT]: 'top-0 start-0 ps-3 pt-3',
    [ToastPositionEnum.TOP_CENTER]: 'top-0 start-50 translate-middle-x pt-3',
    [ToastPositionEnum.TOP_RIGHT]: 'top-0 end-0 pe-3 pt-3',
    [ToastPositionEnum.BOTTOM_LEFT]: 'bottom-0 start-0 ps-3 pb-3',
    [ToastPositionEnum.BOTTOM_CENTER]: 'bottom-0 start-50 translate-middle-x pb-3',
    [ToastPositionEnum.BOTTOM_RIGHT]: 'bottom-0 end-0 pe-3 pb-3',
  };

  isTemplate(toast: Toast): boolean {
    return toast.body instanceof TemplateRef;
  }

  removeToast(toast: Toast): void {
    this.toastService.remove(toast);
  }

  onNavigateToToastSettings(): void {
    this.modalService.dismissAll();
    this.router.navigateByUrl(`/${GeneralRoutePaths.USER}/${SettingsRoutePath.ROOT}/${SettingsRoutePath.SYSTEM}`);
  }
}
